import type { FeedbackRun, ProgressReport } from './report';

export type FlowBlock = 'banner' | 'nothing' | 'history' | 'skills' | 'finding' | 'access' | 'writing' | 'speaking' | 'samples' | 'nextSteps' | 'feedback' | `feedback:${string}`;

export type SectionId = 'history' | 'skills' | 'finding' | 'access' | 'writing' | 'speaking' | 'nextSteps' | 'feedback';

export const REPORT_SECTIONS: readonly { id: SectionId; label: string }[] = [
  { id: 'history', label: 'Benchmark growth history' },
  { id: 'skills', label: 'Indicator scores' },
  { id: 'finding', label: 'Summary' },
  { id: 'access', label: 'ACCESS test results' },
  { id: 'writing', label: 'Writing sample' },
  { id: 'speaking', label: 'Speaking sample' },
  { id: 'nextSteps', label: 'Next steps' },
  { id: 'feedback', label: 'Benchmark feedback' },
];

export function sectionOf(kind: FlowBlock): string | null {
  const head = kind.split(':')[0]!;
  return head === 'banner' || head === 'nothing' ? null : head;
}

function listed(section: string): section is SectionId {
  return REPORT_SECTIONS.some((entry) => entry.id === section);
}

export function chosenBlocks(blocks: FlowBlock[], chosen: ReadonlySet<SectionId>): FlowBlock[] {
  return blocks.flatMap((kind): FlowBlock[] => {
    if (kind === 'banner') return [kind];
    if (kind === 'nothing') return [];
    if (kind === 'samples') {
      const writing = chosen.has('writing');
      const speaking = chosen.has('speaking');
      if (writing && speaking) return ['samples'];
      if (writing) return ['writing'];
      if (speaking) return ['speaking'];
      return [];
    }
    const section = sectionOf(kind);
    if (section === null || !listed(section)) return [kind];
    return chosen.has(section) ? [kind] : [];
  });
}

export function printsNothing(blocks: FlowBlock[], chosen: ReadonlySet<SectionId>): boolean {
  return chosenBlocks(blocks, chosen).every((kind) => kind === 'banner');
}

export function hasSamples(report: ProgressReport): boolean {
  return report.samples.writing.length > 0 || report.samples.speaking.length > 0;
}

// The sections of a report in reading order, leaving out any with nothing to
// show. One writing sample and one speaking sample are each a half-width
// column, so they share a block side by side.
export function flowBlocks(report: ProgressReport): FlowBlock[] {
  const { benchmarks, access, samples } = report;
  const blocks: FlowBlock[] = ['banner'];
  if (benchmarks.shown.length === 0 && access === null && !hasSamples(report)) blocks.push('nothing');
  if (benchmarks.history.length >= 2) blocks.push('history');
  if (benchmarks.shown.length > 0) blocks.push('skills');
  if (benchmarks.narrative.length > 0) blocks.push('finding');
  if (access !== null) blocks.push('access');
  if (samples.writing.length === 1 && samples.speaking.length === 1) {
    blocks.push('samples');
  } else {
    if (samples.writing.length > 0) blocks.push('writing');
    if (samples.speaking.length > 0) blocks.push('speaking');
  }
  if (report.nextSteps) blocks.push('nextSteps');
  if (report.feedback) blocks.push('feedback');
  return blocks;
}

// Fills each page in order. A block never splits: one that does not fit on the
// current page moves whole to the next.
export function packPages(heights: number[], capacity: number): number[][] {
  const pages: number[][] = [[]];
  let used = 0;
  heights.forEach((height, i) => {
    const page = pages[pages.length - 1]!;
    if (page.length > 0 && used + height > capacity) {
      pages.push([i]);
      used = height;
    } else {
      page.push(i);
      used += height;
    }
  });
  return pages;
}

export type FeedbackPart = {
  key: `feedback:${string}`;
  runs: FeedbackRun[];
  opensSection: boolean;
  opensParagraph: boolean;
  closesSection: boolean;
};

export function feedbackParts(report: ProgressReport): FeedbackPart[] {
  const paragraphs = report.feedback?.paragraphs ?? [];
  return paragraphs.map((runs, i) => ({
    key: `feedback:${i}` as const,
    runs,
    opensSection: i === 0,
    opensParagraph: true,
    closesSection: i === paragraphs.length - 1,
  }));
}

export function isFeedbackPart(kind: FlowBlock): boolean {
  return kind.startsWith('feedback:');
}

export function splitOversized(blocks: FlowBlock[], heightOf: (kind: FlowBlock) => number, capacity: number, parts: FeedbackPart[]): FlowBlock[] {
  if (parts.length === 0 || heightOf('feedback') <= capacity) return blocks;
  return blocks.flatMap((kind) => (kind === 'feedback' ? parts.map((part) => part.key) : [kind]));
}

export function oversizedParts(parts: FeedbackPart[], heightOf: (kind: FlowBlock) => number, capacity: number): FeedbackPart[] | null {
  const index = parts.findIndex((part) => heightOf(part.key) > capacity && wordCount(part.runs) > 1);
  if (index < 0) return null;
  const [head, tail] = halve(parts[index]!);
  return [...parts.slice(0, index), head, tail, ...parts.slice(index + 1)];
}

type Word = { text: string; run: FeedbackRun };

function words(runs: FeedbackRun[]): Word[] {
  return runs.flatMap((run) => run.text.split(/(?<= )/).filter((piece) => piece.length > 0).map((piece) => ({ text: piece, run })));
}

function wordCount(runs: FeedbackRun[]): number {
  return words(runs).filter((word) => word.text.trim().length > 0).length;
}

function runsOf(pieces: Word[]): FeedbackRun[] {
  const out: Word[] = [];
  pieces.forEach(({ text, run }) => {
    const last = out[out.length - 1];
    if (last && last.run === run) {
      last.text += text;
    } else {
      out.push({ text, run });
    }
  });
  return out.map(({ text, run }) => ({ ...run, text }));
}

function halve(part: FeedbackPart): [FeedbackPart, FeedbackPart] {
  const all = words(part.runs);
  const cut = Math.ceil(all.length / 2);
  const head = runsOf(all.slice(0, cut));
  head[head.length - 1]!.text = head[head.length - 1]!.text.trimEnd();
  return [
    { key: `${part.key}/a`, runs: head, opensSection: part.opensSection, opensParagraph: part.opensParagraph, closesSection: false },
    { key: `${part.key}/b`, runs: runsOf(all.slice(cut)), opensSection: false, opensParagraph: false, closesSection: part.closesSection },
  ];
}
